import { useState } from "react";
import { UserPlus } from "lucide-react";
import { useTeamMembers } from "@/lib/invitations";
import { useUserMe, useEmailVerified } from "@/lib/user-me";
import { Button } from "@/components/ui/button";
import { InviteTeammateDialog } from "@/components/v2/InviteTeammateDialog";
import { UserAvatar } from "@/components/v2/UserAvatar";

/**
 * `/workspace/team` — Pencil `Ge5aq`. Lists every user row for the
 * calling tenant (active members, pending invites, suspended) and
 * lets owners invite teammates. Backed by `GET /tenants/me/team`
 * and `POST /tenants/me/invitations`.
 */
export function WorkspaceTeam() {
  const team = useTeamMembers();
  const me = useUserMe();
  const emailVerified = useEmailVerified();
  const [inviteOpen, setInviteOpen] = useState(false);

  const members = team.data?.members ?? [];
  const myId = me.data?.id;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <p className="text-sm font-semibold tracking-tight">
              Team members
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              People with access to this workspace.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => setInviteOpen(true)}
          disabled={!emailVerified}
          title={emailVerified ? undefined : "Verify your email first"}
          className="gap-1.5"
        >
          <UserPlus className="h-3.5 w-3.5" /> Invite teammate
        </Button>
      </div>

      <section className="rounded-lg border bg-card divide-y">
        {team.isLoading && (
          <p className="px-5 py-6 text-sm text-muted-foreground">Loading…</p>
        )}
        {!team.isLoading &&
          members.map((m) => (
            <MemberRow
              key={m.id}
              avatarUrl={m.avatarUrl}
              name={
                [m.firstName, m.lastName].filter(Boolean).join(" ").trim() ||
                m.email
              }
              email={m.email}
              role={m.role}
              status={m.status}
              you={m.id === myId}
              meta={metaLine(m.status, m.invitedAt, m.lastLoginAt)}
            />
          ))}
      </section>

      {!team.isLoading && members.length === 1 && members[0].id === myId && (
        <p className="text-xs text-muted-foreground text-center">
          You're the only member —{" "}
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            disabled={!emailVerified}
            title={emailVerified ? undefined : "Verify your email first"}
            className="text-primary hover:underline font-medium disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
          >
            invite someone to get started
          </button>
          .
        </p>
      )}

      <section className="rounded-lg border bg-card">
        <div className="px-5 py-4 border-b border-border">
          <p className="text-sm font-semibold tracking-tight">
            Roles &amp; permissions
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Reference for what each role can do.
          </p>
        </div>
        <div className="divide-y">
          <Role
            name="Owner"
            desc="Full access. Manages billing, plan, and the team. Cannot be removed."
          />
          <Role
            name="Admin"
            desc="Manage bookings, services, inventory, and team. No billing access."
          />
          <Role
            name="Member"
            desc="Read + edit bookings and customers. Can verify payments."
          />
        </div>
      </section>

      <InviteTeammateDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function MemberRow({
  avatarUrl,
  name,
  email,
  role,
  status,
  you,
  meta,
}: {
  avatarUrl: string | null | undefined;
  name: string;
  email: string;
  role: string;
  status: string;
  you?: boolean;
  meta?: string;
}) {
  const initials = computeInitials(name, email);
  return (
    <div className="flex items-center gap-3 px-5 py-3">
      <UserAvatar src={avatarUrl} fallback={initials} size="md" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium truncate">{name}</p>
          {you && (
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded px-1.5 py-0.5">
              You
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {email}
          {meta && <span className="ml-2">· {meta}</span>}
        </p>
      </div>
      <span className="text-xs text-muted-foreground capitalize w-16 text-right">
        {role}
      </span>
      <StatusPill status={status} />
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const lower = status.toLowerCase();
  const cls =
    lower === "active"
      ? "bg-trend-positive/10 text-trend-positive"
      : lower === "invited"
        ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
        : "bg-muted text-muted-foreground";
  return (
    <span
      className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 font-semibold ${cls}`}
    >
      {lower}
    </span>
  );
}

function Role({ name, desc }: { name: string; desc: string }) {
  return (
    <div className="px-5 py-3">
      <p className="text-sm font-medium">{name}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
    </div>
  );
}

function computeInitials(name: string, email: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  if (parts[0]?.length) return parts[0].slice(0, 2).toUpperCase();
  return (email.charAt(0) || "?").toUpperCase();
}

function metaLine(
  status: string,
  invitedAt: string | null | undefined,
  lastLoginAt: string | null | undefined,
): string | undefined {
  if (status === "invited" && invitedAt) {
    return `invited ${formatRelative(invitedAt)}`;
  }
  if (lastLoginAt) {
    return `last seen ${formatRelative(lastLoginAt)}`;
  }
  return undefined;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return new Date(iso).toLocaleDateString();
}
